import { cx } from "@linaria/core";
import { TransitionGroup } from "react-transition-group";

import * as styles from "./atom.left-graphic.style";
import {
  AudioManager,
  useMatchBreakpoint,
  breakpoints,
  PostloadedImage,
  CSSRefTransition,
  AlertIcon,
  PauseIcon,
  PlayIcon,
  describeArcPath,
} from "@pm2/compat";

type Props = {
  imageSrc?: string;

  audio: AudioManager;
  disabled: boolean;
};

export function LeftGraphic(props: Props) {
  const isMobile = useMatchBreakpoint(breakpoints.mobile);

  const onClick = () => {
    if (props.audio.isLoading || props.audio.isPlaying) {
      props.audio.pause();
    } else {
      props.audio.resume();
    }
  };

  const progress =
    (props.audio.timeElapsedMs ?? 0) / (props.audio.durationMs ?? 1);

  const disabled = props.disabled || props.audio.didError;
  const withImage = !!props.imageSrc && !props.audio.didError;

  return (
    <div
      className={styles.wrapper}
      data-disabled={disabled}
      data-error={props.audio.didError}
      data-with-image={withImage}
    >
      {withImage && (
        <div className={styles.imageWrapper}>
          <PostloadedImage src={props.imageSrc} alt="" sizes="150px" />
        </div>
      )}

      <button
        className={cx(
          styles.button,
          props.audio.isPlaying && styles.modifiers.isPlaying.className,
        )}
        tabIndex={-1}
        aria-hidden
        onClick={onClick}
        disabled={disabled}
      >
        <TransitionGroup component={null}>
          <CSSRefTransition
            key={`icon-${props.audio.isPlaying}-${disabled}`}
            timeout={styles.crossFadeDurationMs}
            classNames={styles.icon}
          >
            {(ref) => (
              <div ref={ref} className={styles.icon}>
                {props.audio.didError ? (
                  <AlertIcon data-type="alert" width={isMobile ? 24 : 38} />
                ) : props.audio.isPlaying ? (
                  <PauseIcon data-type="pause" width={isMobile ? 14 : 20} />
                ) : (
                  <PlayIcon data-type="play" width={isMobile ? 22 : 32} />
                )}
              </div>
            )}
          </CSSRefTransition>

          {props.audio.isPlaying && (
            <CSSRefTransition
              timeout={styles.crossFadeDurationMs}
              classNames={styles.asyncProgressCircle}
            >
              {(ref) => (
                <svg
                  ref={ref}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox={`0 0 84 84`}
                  className={styles.asyncProgressCircle}
                >
                  <path
                    key="progress"
                    d={describeArcPath(
                      84,
                      4,
                      Math.min(359.99, Math.round(progress * 360)),
                    )}
                  />
                </svg>
              )}
            </CSSRefTransition>
          )}
        </TransitionGroup>
      </button>
    </div>
  );
}
